import React, { Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

/**
 * Slideover — right-side slide-over panel + Modal primitive (LAT-29 / LAT-31).
 *
 * Single overlay primitive used everywhere a drawer or modal is needed.
 * Built on Headless UI's `Dialog`, which gives us:
 *   - Focus trap inside the panel
 *   - Backdrop click + Esc to close
 *   - One overlay layer at a time (open this → any open menu/popover closes,
 *     because Headless UI's Dialog moves focus & traps it).
 *   - Inert background — pointer events outside the dialog are ignored
 *     while it's open.
 *
 * Two layouts:
 *   <Slideover>          right-side slide-over panel (default)
 *   <Slideover.Modal>    centered modal dialog
 *
 * Standard chrome (consistent across every surface):
 *   ┌──────────────────────────────┐
 *   │ Title                     ✕  │  ← header (sticky)
 *   ├──────────────────────────────┤
 *   │ children                     │  ← scrollable body
 *   ├──────────────────────────────┤
 *   │ [Cancel]  [Save]             │  ← footer (sticky, optional)
 *   └──────────────────────────────┘
 *
 * Props:
 *   open        bool
 *   onClose()   fn    — Esc, backdrop click, X-button
 *   title       node  — H2 in header
 *   subtitle    node
 *   footer      node  — usually action buttons
 *   width       'sm' | 'md' | 'lg' | 'xl' | css-width
 *   initialFocus React ref to focus on open (Headless UI's initialFocus)
 *
 * Sub-component:
 *   <Slideover.Modal …>  same API but centered modal
 */

const WIDTHS = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

function Slideover({
  open,
  onClose,
  title,
  subtitle,
  footer,
  width = 'md',
  initialFocus,
  children,
  className,
  panelClassName,
  closeOnBackdrop = true,
  hideClose = false,
}) {
  // Lock body scroll when open (Headless UI handles focus, we handle scroll
  // for the rare case where multiple stacked transforms would otherwise leak).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const widthCls = WIDTHS[width] || '';
  const widthStyle = WIDTHS[width] ? undefined : { maxWidth: width };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={closeOnBackdrop ? onClose : () => {}}
        initialFocus={initialFocus}
      >
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 transition-opacity"
            style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)' }}
          />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel
                  className={clsx(
                    'pointer-events-auto w-screen flex flex-col h-full shadow-2xl',
                    widthCls,
                    panelClassName,
                    className
                  )}
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    ...widthStyle,
                  }}
                >
                  {(title || !hideClose) && (
                    <div
                      className="flex items-start justify-between gap-4 px-6 py-4 border-b flex-shrink-0"
                      style={{ borderColor: 'var(--color-border-subtle)' }}
                    >
                      <div className="min-w-0">
                        {title && (
                          <Dialog.Title
                            className="truncate"
                            style={{
                              fontFamily: 'var(--font-display)',
                              fontSize: 18,
                              lineHeight: 1.3,
                              fontWeight: 600,
                              color: 'var(--color-text-default)',
                            }}
                          >
                            {title}
                          </Dialog.Title>
                        )}
                        {subtitle && (
                          <p
                            className="mt-0.5 text-sm"
                            style={{ color: 'var(--color-text-muted)' }}
                          >
                            {subtitle}
                          </p>
                        )}
                      </div>
                      {!hideClose && (
                        <button
                          type="button"
                          onClick={onClose}
                          className="flex-shrink-0 -mr-2 -mt-1 inline-flex items-center justify-center h-9 w-9 rounded hover:bg-[color:var(--neutral-100)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-action-primary)]"
                          aria-label="Close"
                        >
                          <XMarkIcon className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
                        </button>
                      )}
                    </div>
                  )}

                  <div className="flex-1 overflow-y-auto px-6 py-4">
                    {children}
                  </div>

                  {footer && (
                    <div
                      className="flex items-center justify-end gap-2 px-6 py-3 border-t flex-shrink-0"
                      style={{
                        borderColor: 'var(--color-border-subtle)',
                        backgroundColor: 'var(--color-surface-sunken)',
                      }}
                    >
                      {footer}
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

/* ------------------------------------------------------------------ *
 * Modal — centered version of the same primitive
 * ------------------------------------------------------------------ */
function Modal({
  open,
  onClose,
  title,
  subtitle,
  footer,
  width = 'md',
  initialFocus,
  children,
  className,
  panelClassName,
  closeOnBackdrop = true,
  hideClose = false,
}) {
  const widthCls = WIDTHS[width] || '';
  const widthStyle = WIDTHS[width] ? undefined : { maxWidth: width };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={closeOnBackdrop ? onClose : () => {}}
        initialFocus={initialFocus}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 transition-opacity"
            style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)' }}
          />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  'relative w-full shadow-2xl',
                  widthCls,
                  panelClassName,
                  className
                )}
                style={{
                  backgroundColor: 'var(--color-surface)',
                  borderRadius: 'var(--radius-lg)',
                  ...widthStyle,
                }}
              >
                {(title || !hideClose) && (
                  <div
                    className="flex items-start justify-between gap-4 px-6 py-4 border-b"
                    style={{ borderColor: 'var(--color-border-subtle)' }}
                  >
                    <div className="min-w-0">
                      {title && (
                        <Dialog.Title
                          style={{
                            fontFamily: 'var(--font-display)',
                            fontSize: 'var(--text-h3)',
                            fontWeight: 'var(--fw-h3)',
                            color: 'var(--color-text-default)',
                          }}
                        >
                          {title}
                        </Dialog.Title>
                      )}
                      {subtitle && (
                        <p
                          className="mt-0.5 text-sm"
                          style={{ color: 'var(--color-text-muted)' }}
                        >
                          {subtitle}
                        </p>
                      )}
                    </div>
                    {!hideClose && (
                      <button
                        type="button"
                        onClick={onClose}
                        className="flex-shrink-0 -mr-2 inline-flex items-center justify-center h-8 w-8 rounded hover:bg-[color:var(--neutral-100)] focus:outline-none focus-visible:ring-2"
                        aria-label="Close"
                      >
                        <XMarkIcon className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
                      </button>
                    )}
                  </div>
                )}

                <div className="px-6 py-4">{children}</div>

                {footer && (
                  <div
                    className="flex items-center justify-end gap-2 px-6 py-3 border-t"
                    style={{
                      borderColor: 'var(--color-border-subtle)',
                      backgroundColor: 'var(--color-surface-sunken)',
                      borderBottomLeftRadius: 'var(--radius-lg)',
                      borderBottomRightRadius: 'var(--radius-lg)',
                    }}
                  >
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

Slideover.Modal = Modal;

export default Slideover;
export { Modal };
